import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, Heart, PackageOpen, ArrowRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { fmt, fmtPerUnit, fmtQty } from "@/lib/api";
import { ShinyButton } from "@/components/ui/ShinyButton";

export default function CartDrawer() {
  const { drawerOpen, setDrawerOpen, cart, setQty, removeItem, saveForLater } = useStore();
  const navigate = useNavigate();

  const go = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-md flex-col border-l border-line bg-surface"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-line px-6 py-5">
              <div>
                <h2 className="font-display text-lg font-semibold tracking-tight">YOUR CART</h2>
                <p className="text-xs text-3" data-testid="cart-drawer-count">
                  {cart.count} {cart.count === 1 ? "item" : "items"}
                </p>
              </div>
              <button
                data-testid="cart-drawer-close"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-2 transition-colors hoverable-t1"
                aria-label="Close cart"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {cart.items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-drawer-empty">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-line bg-hover text-amber-500">
                  <PackageOpen className="h-7 w-7" strokeWidth={1.6} />
                </span>
                <h3 className="font-display text-xl font-semibold tracking-tight">YOUR CART IS EMPTY</h3>
                <p className="text-sm text-3">
                  Find the packaging and shipping essentials you need.
                </p>
                <button
                  data-testid="cart-drawer-continue"
                  onClick={() => go("/shop")}
                  className="mt-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 space-y-3 overflow-y-auto px-5 py-5" data-lenis-prevent>
                  {cart.items.map((item) => (
                    <div key={`${item.product_id}-${item.sku}-${item.channel || "regular"}`} className="flex gap-4 rounded-xl border border-line bg-surface p-3 shadow-card" data-testid={`cart-item-${item.sku}`}>
                      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-line bg-white p-1.5">
                        <img
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                          className="h-full w-full object-contain"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-1">{item.name}</p>
                        <p className="mt-0.5 text-xs text-3">{item.variant_label}</p>
                        <p className="mt-0.5 text-[11px] text-3" data-testid={`cart-item-unitprice-${item.sku}`}>
                          {fmtPerUnit(item.unit_price, item.unit)} × {fmtQty(item.qty, item.unit)}
                          {item.prime_priced && <span className="ml-1.5 rounded-full border border-[#E4B66D]/50 bg-[#242521] px-1.5 py-px text-[8px] font-extrabold text-[#E4B66D]" data-testid={`cart-item-prime-${item.sku}`}>PRIME</span>}
                        </p>
                        {item.member_locked && (
                          <p className="mt-1 text-[10px] font-bold text-[#A96C1E]" data-testid={`cart-item-locked-${item.sku}`}>
                            PRIME Store item — join PRIME to buy, or remove it
                          </p>
                        )}
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center rounded-lg border border-line">
                            <button
                              data-testid={`cart-item-minus-${item.sku}`}
                              onClick={() => {
                                const inc = item.qty_increment || 1;
                                const moq = item.min_order_qty || 1;
                                const next = item.qty - inc;
                                if (next < moq) removeItem(item.product_id, item.sku, item.channel);
                                else setQty(item.product_id, item.sku, next, item.channel);
                              }}
                              className="flex h-7 w-7 items-center justify-center text-2 hoverable-t1"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="min-w-7 px-1 text-center text-xs font-bold" data-testid={`cart-item-qty-${item.sku}`}>{item.qty.toLocaleString("en-IN")}</span>
                            <button
                              data-testid={`cart-item-plus-${item.sku}`}
                              onClick={() => setQty(item.product_id, item.sku, item.qty + (item.qty_increment || 1), item.channel)}
                              className="flex h-7 w-7 items-center justify-center text-2 hoverable-t1"
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="text-sm font-bold text-amber-500">{fmt(Math.round(item.unit_price * item.qty * 100) / 100)}</span>
                        </div>
                        <div className="mt-2 flex gap-3">
                          <button
                            data-testid={`cart-item-save-${item.sku}`}
                            onClick={() => saveForLater(item)}
                            className="flex items-center gap-1 text-[11px] font-medium text-3 transition-colors hover:text-emerald-400"
                          >
                            <Heart className="h-3 w-3" /> Save for later
                          </button>
                          <button
                            data-testid={`cart-item-remove-${item.sku}`}
                            onClick={() => removeItem(item.product_id, item.sku, item.channel)}
                            className="flex items-center gap-1 text-[11px] font-medium text-3 transition-colors hover:text-red-400"
                          >
                            <Trash2 className="h-3 w-3" /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-line bg-surface2 px-6 py-5">
                  <div className="mb-1.5 flex items-center justify-between text-sm">
                    <span className="text-2">Subtotal</span>
                    <span className="font-display text-base font-semibold text-1" data-testid="cart-drawer-subtotal">{fmt(cart.subtotal)}</span>
                  </div>
                  <p className="mb-4 text-[11px] text-3">
                    Shipping, GST and coupons are calculated at checkout.
                  </p>
                  <ShinyButton
                    data-testid="cart-drawer-checkout"
                    onClick={() => go("/checkout")}
                    className="w-full"
                    contentClassName="w-full py-3.5 text-sm font-bold"
                  >
                    Proceed to Checkout
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </ShinyButton>
                  <button
                    data-testid="cart-drawer-view-cart"
                    onClick={() => go("/cart")}
                    className="mt-2 w-full rounded-full border border-strong py-3 text-sm font-bold text-1 transition-colors hover:border-amber-500/50 hover:text-amber-400"
                  >
                    View Full Cart
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
